import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { api, type LiveControl, type LivePresence } from '../api/client';
import { Button, Dialog } from '../components/ui';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';

/** This tab, for live help: kept across reloads so a session carries on after one. */
function tabId(): string {
  const make = () => `tab_${crypto.randomUUID().replace(/-/g, '')}`;
  try {
    const had = sessionStorage.getItem('stitch.tab');
    if (had && /^[A-Za-z0-9_-]{8,64}$/.test(had)) return had;
    const id = make();
    sessionStorage.setItem('stitch.tab', id);
    return id;
  } catch {
    return make();
  }
}
const TAB = typeof window === 'undefined' ? '' : tabId();

const tenantIn = (path: string) => /^\/t\/([^/]+)/.exec(path)?.[1];

interface Live {
  id: string;
  staffName: string;
  control: LiveControl;
}

/**
 * Live help, the customer's side: tells Stitch which page this tab is on (so support can find
 * them), asks before support sees anything, and while support is here shows who, where they're
 * pointing, and a way to stop at any time.
 */
export function LiveHelpCustomer() {
  const { state } = useAuth();
  const { pathname } = useLocation();
  const customer = Boolean(state?.signedIn && state.user && !state.user.staff);
  const [ask, setAsk] = useState<{ id: string; staffName: string } | null>(null);
  const [live, setLive] = useState<Live | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [pings, setPings] = useState<{ key: number; x: number; y: number }[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const answered = useRef(new Set<string>());
  const page = useRef(pathname);
  page.current = pathname;
  const stopSharing = useRef<(() => Promise<void>) | null>(null);
  /** The session this tab is sharing (set as soon as it starts, before the recorder has loaded). */
  const sharing = useRef<string | null>(null);
  /** Sessions this tab has seen end: a check-in answered before the end mustn't bring one back. */
  const finished = useRef(new Set<string>());
  const liveRef = useRef<Live | null>(null);
  liveRef.current = live;

  const end = useCallback((message = '') => {
    const id = sharing.current ?? liveRef.current?.id;
    if (id) finished.current.add(id);
    void stopSharing.current?.();
    stopSharing.current = null;
    sharing.current = null;
    liveRef.current = null;
    setLive(null);
    setPointer(null);
    setNote(message);
  }, []);

  const share = useCallback(
    async (id: string, staffName: string) => {
      if (sharing.current === id || finished.current.has(id)) return;
      sharing.current = id;
      setLive({ id, staffName, control: 'none' });
      liveRef.current = { id, staffName, control: 'none' };
      const { startSharing } = await import('./share');
      if (liveRef.current?.id !== id) return;
      stopSharing.current = startSharing(id, TAB, {
        status: (s) => setLive((l) => (l && l.id === id ? { ...l, control: s.control } : l)),
        pointer: setPointer,
        ping: (p) => {
          const key = Date.now() + Math.random();
          setPings((list) => [...list.slice(-4), { key, ...p }]);
          window.setTimeout(() => setPings((list) => list.filter((x) => x.key !== key)), 1400);
        },
        ended: () => end(`${staffName} has left. Live help has ended.`)
      });
    },
    [end]
  );

  const handle = useCallback(
    (r: LivePresence) => {
      if (r.session?.status === 'live') {
        void share(r.session.id, r.session.staffName);
      } else if (sharing.current) {
        end('Live help has ended.');
      }
      setAsk(r.ask && !answered.current.has(r.ask.id) ? r.ask : null);
    },
    [share, end]
  );

  // Check in every few seconds while the tab is open (less often when it's in the background).
  useEffect(() => {
    if (!customer) return;
    let timer: number | undefined;
    let gone = false;
    const beat = async () => {
      window.clearTimeout(timer);
      try {
        const tenant = tenantIn(page.current);
        handle(await api.livePresence({ tabId: TAB, page: page.current, visible: !document.hidden, ...(tenant ? { tenant: decodeURIComponent(tenant) } : {}) }));
      } catch {
        // Offline for a moment: try again on the next beat.
      }
      if (!gone) timer = window.setTimeout(() => void beat(), document.hidden ? 30_000 : 8_000);
    };
    void beat();
    const onVisible = () => void beat();
    const onLeave = () => {
      // The tab is closing: say so, so support doesn't see someone who has gone.
      void fetch('/api/live/leave', { method: 'POST', keepalive: true, headers: { 'content-type': 'application/json', 'x-stitch-request': '1' }, body: JSON.stringify({ tabId: TAB }) }).catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onLeave);
    return () => {
      gone = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onLeave);
    };
  }, [customer, handle]);

  // A new page: tell Stitch soon, so support sees where they are.
  useEffect(() => {
    if (!customer) return;
    const t = window.setTimeout(() => {
      const tenant = tenantIn(pathname);
      api
        .livePresence({ tabId: TAB, page: pathname, visible: !document.hidden, ...(tenant ? { tenant: decodeURIComponent(tenant) } : {}) })
        .then(handle)
        .catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(t);
  }, [pathname, customer, handle]);

  // Signed out: live help ends with the session.
  useEffect(() => {
    if (!customer && sharing.current) end();
  }, [customer, end]);
  useEffect(() => () => void stopSharing.current?.(), []);

  async function answer(allow: boolean) {
    if (!ask) return;
    setBusy(true);
    answered.current.add(ask.id);
    try {
      const r = await api.liveAnswer(ask.id, TAB, allow);
      setAsk(null);
      if (allow && r.session.status === 'live') void share(r.session.id, r.session.staff.name);
    } catch (err) {
      setAsk(null);
      setNote(err instanceof Error ? err.message : 'That didn’t work. Support can ask again.');
    } finally {
      setBusy(false);
    }
  }

  async function giveControl(allow: boolean) {
    if (!live) return;
    setBusy(true);
    try {
      const r = await api.liveControl(live.id, TAB, allow);
      setLive((l) => (l ? { ...l, control: r.session.control } : l));
    } catch {
      // The stream will bring the right state.
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    if (!live) return;
    const name = live.staffName;
    finished.current.add(live.id);
    // Stop sending first (and let a batch on its way land), then end the session: nothing is
    // sent to a session that's already over.
    const stopping = stopSharing.current?.();
    stopSharing.current = null;
    await stopping?.catch(() => undefined);
    try {
      await api.liveEnd(live.id);
    } catch {
      // Ending locally is what matters: nothing more is sent.
    }
    end(`You stopped sharing with ${name}.`);
  }

  if (!customer) return null;
  const first = (name: string) => name.split(/\s+/)[0] || name;

  return (
    <div data-live-ui="">
      {ask && (
        <Dialog
          open
          onClose={() => void answer(false)}
          title="Stitch support would like to see your screen"
          subtitle={`${ask.staffName} from Stitch is asking to join, to help you.`}
          foot={
            <>
              <Button variant="primary" icon="eye" disabled={busy} onClick={() => void answer(true)} data-autofocus>
                Let {first(ask.staffName)} see it
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => void answer(false)}>
                Not now
              </Button>
            </>
          }
        >
          <ul className="live-promise">
            <li>
              <Icon name="eye" size={17} /> They’ll see this Stitch tab only: not your other tabs, apps or the rest of your computer.
            </li>
            <li>
              <Icon name="lock" size={17} /> Passwords stay hidden, and nothing is recorded.
            </li>
            <li>
              <Icon name="pointer" size={17} /> They can point things out. They can only click or type if you say so.
            </li>
            <li>
              <Icon name="x" size={17} /> You can stop at any time.
            </li>
          </ul>
        </Dialog>
      )}
      {live && live.control === 'asked' && (
        <Dialog
          open
          onClose={() => void giveControl(false)}
          title={`Let ${first(live.staffName)} click and type for you?`}
          subtitle="Handy when it’s quicker to show than to explain."
          foot={
            <>
              <Button variant="primary" disabled={busy} onClick={() => void giveControl(true)} data-autofocus>
                Let them
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => void giveControl(false)}>
                No thanks
              </Button>
            </>
          }
        >
          <p>
            You’ll see everything they do, and you can take back control at any time from the bar at the top of the screen. Anything they change is saved under your name and
            theirs.
          </p>
        </Dialog>
      )}
      {live && (
        <>
          <div className={`live-frame${live.control === 'granted' ? ' driving' : ''}`} aria-hidden="true" />
          <div className="live-bar" role="status">
            <span className="live-dot" aria-hidden="true" />
            <span className="live-bar-text">
              <strong>{live.staffName}</strong> from Stitch {live.control === 'granted' ? 'has control' : 'can see this tab'}
            </span>
            {live.control === 'granted' && (
              <button type="button" className="live-bar-btn" onClick={() => void giveControl(false)} disabled={busy}>
                Take back control
              </button>
            )}
            <button type="button" className="live-bar-btn stop" onClick={() => void stop()}>
              Stop sharing
            </button>
          </div>
          {pointer && (
            <div className="live-pointer" style={{ transform: `translate(${pointer.x}px, ${pointer.y}px)` }} aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 3l7.5 18 2.4-7.1L21 11.5z" />
              </svg>
              <span>{first(live.staffName)}</span>
            </div>
          )}
          {pings.map((p) => (
            // The `translate` property, not transform: the ring's scale animation would scale a
            // transform's translation too, and the ring flew off the clicked spot (3053).
            <span key={p.key} className="live-ping" style={{ translate: `${p.x}px ${p.y}px` }} aria-hidden="true" />
          ))}
        </>
      )}
      {note && !live && (
        <div className="live-bar ended" role="status">
          <span className="live-bar-text">{note}</span>
          <button type="button" className="live-bar-btn" onClick={() => setNote('')}>
            OK
          </button>
        </div>
      )}
    </div>
  );
}
